import React, { useState, useCallback, useLayoutEffect, forwardRef } from 'react'
import { createEditor } from 'slate'
import { withHistory } from 'slate-history'
import { Editable, Slate, withReact } from 'slate-react'

import { NoDataPlaceHolder, TemplateSelector } from '../PatientFolder/behavioral/components'

import { PermissionsContext } from './context'
import styles from './index.module.scss'
import { Menu } from './Menu'
import { renderElement } from './renderElement'
import { renderLeaf } from './renderLeaf'
import { serializeContent } from './serializeContent'
import { withCheckboxListItems } from './withCheckboxListItems'
import { withInlines } from './withInlines'

/**
 *
 * @param {import('slate').BaseOperation} operation
 */
function isASTOperation (operation) {
  return operation.type !== 'set_selection'
}

export const RichTextEditor = forwardRef(({
  value,
  canEdit,
  readOnly = false,
  template,
  actions,
  statuses,
  selectedStatus,
  empty,
  ...rest
}, ref) => {
  const [ editor ] = useState(() => withHistory(withCheckboxListItems(withInlines(withReact(createEditor())))))

  const { onChange } = actions

  useLayoutEffect(() => {
    editor.children = value
    editor.onChange()
  }, [ editor, value ])

  const handleChange = useCallback(value => {
    const isASTChange = editor.operations.some(isASTOperation)

    if (!isASTChange) {
      return
    }

    onChange?.(value)
  }, [ editor, onChange ])

  return (
    <div
      ref={ref}
      className='w-full'
    >
      <Slate
        editor={editor}
        initialValue={value}
        onChange={handleChange}
      >
        <Menu
          isReadOnly={readOnly}
          actions={actions}
          selectedStatus={selectedStatus}
          statuses={statuses}
        />
        <PermissionsContext.Provider value={{ canEdit }}>
          <div className='flex flex-row w-full relative'>
            {typeof template.onChange === 'function' && (
              <PlaceHolder
                onChange={template.onChange}
                templates={template.templates}
              />
            )}
            { typeof template.onChange !== 'function' && empty && (
              <EmptyPlaceholder />
            )}
            <div className='flex-1'>
              <Editable
                readOnly={readOnly}
                /* eslint-disable-next-line jsx-a11y/no-autofocus */
                autoFocus={!readOnly}
                renderElement={renderElement}
                renderLeaf={renderLeaf}
                className={styles.editable}
                {...rest}
              />
            </div>
          </div>
        </PermissionsContext.Provider>

      </Slate>
    </div>
  )
})

RichTextEditor.displayName = 'RichTextEditor'

export { serializeContent }

function PlaceHolder ({ onChange, templates }) {
  return (
    <div
      className='absolute top-0 left-0 w-full'
      style={{ padding: '1.5rem 2.6rem 0rem' }}
    >
      <div className='flex flex-row flex-wrap items-center gap-1 justify-between'>
        <span className='text-[16px] text-[#6C727F] select-none relative'>
          Start typing or select a template
        </span>
        <TemplateSelector
          onChange={onChange}
          templates={templates}
        />
      </div>
    </div>
  )
}

function EmptyPlaceholder () {
  return (
    <div
      className='absolute top-0 z-10 left-0 w-full h-full'
      style={{ padding: '1.5rem 2.6rem 0rem' }}
    >
      <NoDataPlaceHolder title='Empty Note' />
    </div>
  )
}
